import { router } from 'expo-router';
import * as React from 'react';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Regular,
    isNonMobileWeb,
    isWeb,
    ScrollView,
    View,
    YStack,
} from 'smoothie';

import { useUserAuthorization } from '../../../auth/hooks/useUserAuthorization';
import {
    canUserCreateBankAccount,
    formatInterestRateOrYieldPercent,
} from '../../../common/utils/bankingUtils';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import type { User } from '../../../user/types/User';
import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import { KYCVerificationStatus } from '../../types/BankAccount';
import type InterestRate from '../../types/InterestRate';
import { BankDisclosureFootnote } from '../Disclosures';
import LegacySpaceCard from './LegacySpaceCard';

const LegacyEmptySpaces = ({
    currentInterestRate,
    user,
}: {
    currentInterestRate: InterestRate | undefined;
    user: User;
}) => {
    const { accessToRestrictedCapabilities } = useUserAuthorization();
    const { syncteraKycStatus } = user;

    return (
        <ScrollView
            contentContainerStyle={{
                flexGrow: 1,
            }}
        >
            <YStack
                flex={1}
                justifyContent={isWeb ? 'flex-start' : 'space-between'}
                marginHorizontal={16}
                marginTop={40}
                marginBottom={12}
                $sm={{ maxWidth: 500, alignSelf: 'center', marginTop: 100 }}
            >
                <YStack alignItems="center">
                    <View marginBottom={80}>
                        <H2Regular textAlign="center">
                            Start earning{' '}
                            {formatInterestRateOrYieldPercent(
                                currentInterestRate?.apy ?? 0,
                            )}{' '}
                            APY{'\n'} on your cash
                        </H2Regular>
                    </View>
                    <ExampleSpaceCards />
                    <View marginTop={36}>
                        <BodySmallRegular textAlign="center">
                            Organize your savings into categories and{'\n'}
                            earn more on your cash with Fruitful
                        </BodySmallRegular>
                    </View>
                </YStack>
                <YStack
                    marginTop={isNonMobileWeb ? 100 : isWeb ? 50 : 'auto'}
                    paddingBottom={isWeb ? 50 : 0}
                    gap={12}
                >
                    <View paddingHorizontal={44}>
                        <BankDisclosureFootnote />
                    </View>
                    {accessToRestrictedCapabilities.openBankAccount ? (
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text={getButtonCta(syncteraKycStatus)}
                            disabled={isCtaDisabled(syncteraKycStatus)}
                            onPress={() => {
                                const { allowed } =
                                    canUserCreateBankAccount(user);
                                if (allowed) {
                                    router.navigate(
                                        '/banking/create-edit-space',
                                    );
                                } else {
                                    router.navigate('/banking/kyc');
                                }
                            }}
                        />
                    ) : (
                        <BodyMediumEmphasized
                            textAlign="center"
                            marginTop={16}
                            color="$gray800"
                        >
                            You are not authorized to open an account
                        </BodyMediumEmphasized>
                    )}
                </YStack>
            </YStack>
        </ScrollView>
    );
};

const isCtaDisabled = (syncteraKycStatus: KYCVerificationStatus | null) =>
    syncteraKycStatus != null &&
    [
        KYCVerificationStatus.PENDING,
        KYCVerificationStatus.REVIEW,
        KYCVerificationStatus.REJECTED,
    ].includes(syncteraKycStatus);

const getButtonCta = (syncteraKycStatus: KYCVerificationStatus | null) => {
    switch (syncteraKycStatus) {
        case KYCVerificationStatus.PENDING:
        case KYCVerificationStatus.REVIEW:
            return 'Your account is pending';
        case KYCVerificationStatus.REJECTED:
            return `Please reach out to ${SUPPORT_EMAIL}`;
        default:
            return 'Open an account';
    }
};

const ExampleSpaceCards = () => {
    const details: Array<Partial<BankAccountDisplayDetail>> = [
        {
            nickname: 'Home renovation',
            icon: 'home',
            color: 'orange',
            availableToWithdraw: '$25,000.00',
            interestEarned: '$97.11',
        },
        {
            nickname: 'Rainy day',
            icon: 'cloudRaining',
            color: 'greenleaf',
            availableToWithdraw: '$13,000.00',
            interestEarned: '$50.50',
        },
        {
            nickname: 'Summer travel',
            icon: 'globe',
            color: 'peach',
            availableToWithdraw: '$13,000.00',
            interestEarned: '$50.50',
        },
    ];

    return (
        <YStack height={150} scale={0.55} marginLeft={72}>
            {details[0] && details[1] && details[2] && (
                <>
                    <LegacySpaceCard
                        bankAccountDetail={details[0]}
                        width={300}
                        zIndex={3}
                        marginTop={-120}
                    />
                    <LegacySpaceCard
                        bankAccountDetail={details[1]}
                        width={300}
                        zIndex={1}
                        marginTop={-80}
                        marginLeft={-120}
                        rotate="-10deg"
                    />
                    <LegacySpaceCard
                        bankAccountDetail={details[2]}
                        width={300}
                        zIndex={2}
                        marginTop={-180}
                        marginLeft={130}
                        rotate="10deg"
                    />
                </>
            )}
        </YStack>
    );
};

export default LegacyEmptySpaces;
